import React, { useState, useEffect } from "react";
import { Snackbar, IconButton, Alert, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useGridApiRef } from '@mui/x-data-grid-pro';
import ControlledDataGrid from '../../../components/DataGrid/ControlledDataGrid';
import { ProgramFilter, ReportFilterBar } from '../ReportFilters';
import moment from "moment";
import axios from "axios";
import { APIURL } from "../../../config";

export default function ActiveCustomerReport(props) {
    // Use customReportGridId if provided (for custom reports), otherwise use default
    const defaultGridId = 41;
    const [gridId, setGridId] = useState(props.customReportGridId || defaultGridId);
    const apiRef = useGridApiRef();

    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState([]);
    const [schemaColumns, setSchemaColumns] = useState([]);
    const [reportDetails, setReportDetails] = useState({});
    const [isFavorite, setIsFavorite] = useState(false);
    const [successText, setSuccessText] = useState("");
    const [errorText, setErrorText] = useState("");

    const pBusinessType = props.user?.comp_settings.find(setting => setting.feature_value === 'primaryBusinessType')
        ? props.user.comp_settings.filter((setting) => setting.feature_value === "primaryBusinessType")[0].feature_id
        : -1;

    const [selectedProgram, setSelectedProgram] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setIsLoading(true);
        axios
            .get(APIURL + "/jsreport/getAllReports", {
                params: { empID: props.user.emp_id },
            })
            .then((response) => {
                const report = response?.data?.reports.find((ele) => ele.report_name === "Active Customer Report");
                setReportDetails(report);
                setIsFavorite(report ? (report.status === 1 ? true : false) : false);
            });

        axios
            .get(APIURL + "/reports/getActiveCustomers", {
                params: {
                    com_id: props.user.comp_id,
                    emp_id: props.user.emp_id,
                    pBusinessType: pBusinessType,
                    program_id: selectedProgram || undefined,
                },
            })
            .then((response) => {
                setData(response?.data?.data?.length ? response.data.data : []);
                setSchemaColumns(response?.data?.schemaColumns || []);
                setIsLoading(false);
            })
            .catch((err) => {
                console.log(err);
                setIsLoading(false);
            });
    };

    const baseColumns = [
        { field: "cus_id", headerName: "Customer ID", hide: true },
        { field: "cus_name", headerName: "Customer Name", flex: 1 },
        { field: "gender", headerName: "Gender", flex: 1 },
        { field: "cus_address", headerName: "Customer Address", flex: 1 },
        { field: "cus_phone", headerName: "Customer Phone", flex: 1 },
        { field: "cus_email", headerName: "Customer Email", flex: 1 },
        {
            field: "cus_acquired_date",
            headerName: "Customer Acquired Date",
            flex: 1,
            renderCell: (params) => {
                return (
                    <div>{moment(params.value).isValid() ? moment(params.value).utc().format('MM/DD/YYYY') : params.value}</div>
                )
            }
        },
        {
            field: "renewal_date",
            headerName: "Renewal Date",
            flex: 1,
            renderCell: (params) => {
                return (
                    <div>{moment(params.value).isValid() ? moment(params.value).utc().format('MM/DD/YYYY') : params.value}</div>
                )
            }
        },
        { field: "medicaid_eligible", headerName: "Medicaid eligible", flex: 1 },
        { field: "medicaid_id", headerName: "Medicaid ID", flex: 1 },
        { field: "waiver_program", headerName: "Waiver Program", flex: 1 },
        { field: "PrimaryDiagnosisCode", headerName: "Primary Diagnostic Code", flex: 1 },
        {
            field: "MedicaidEndDate",
            headerName: "Medicaid End Date",
            flex: 1,
            renderCell: (params) => {
                return (
                    <div>{moment(params.value).isValid() ? moment(params.value).utc().format('MM/DD/YYYY') : params.value}</div>
                )
            }
        },
        {
            field: "AgencyAssignDate",
            headerName: "Agency Assign Date",
            flex: 1,
            renderCell: (params) => {
                return (
                    <div>{moment(params.value).isValid() ? moment(params.value).utc().format('MM/DD/YYYY') : params.value}</div>
                )
            }
        },
        { field: "County", headerName: "County", flex: 1 },
        {
            field: "LastAnnualVisitDate",
            headerName: "Last Annual Visit",
            flex: 1,
            renderCell: (params) => {
                return (
                    <div>{moment(params.value).isValid() ? moment(params.value).utc().format('MM/DD/YYYY') : params.value}</div>
                )
            }
        },
    ];

    const columns = React.useMemo(() => {
        return [...baseColumns, ...schemaColumns];
    }, [schemaColumns]);

    let rows = data

    const markFavourite = async (data, type) => {
        const payload = {
            report_id: data?.report_id,
            status: type,
            empID: props.user.emp_id
        };

        axios
            .put(APIURL + "/jsreport/markFavorite", payload)
            .then((response) => {
                if (response?.status === 200 && response?.data?.code === 200) {
                    setSuccessText(response?.data?.status_message || "Favorite Modifed Succesfully");
                } else {
                    setErrorText("Something went wrong");
                }

                fetchData();
            });
    }

    return (
        <div style={{ margin: 0, width: '100%', padding: 10, boxSizing: 'border-box' }}>
            <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <ReportFilterBar onSearch={() => fetchData()}>
                        <ProgramFilter
                            comp_id={props.user.comp_id}
                            value={selectedProgram}
                            onChange={setSelectedProgram}
                        />
                    </ReportFilterBar>
                    <Tooltip title={isFavorite ? "Remove from favorite reports" : "Add to favorite reports"}>
                        <img
                            src={isFavorite ? "./star-solid.svg" : "./star-regular.svg"}
                            alt="barchart"
                            style={{
                                fontFamily: "Lato",
                                width: "1.5rem",
                                height: "1.5rem",
                                cursor: "pointer",
                                margin: 5
                            }}
                            onClick={() => markFavourite(reportDetails, isFavorite ? 0 : 1)}
                        />
                    </Tooltip>
                </div>
                <div style={{ height: 'calc(100vh - 250px)' }}>
                    <ControlledDataGrid
                        gridId={gridId}
                        user={props.user}
                        initialState={{ sorting: { sortModel: [{ field: "cus_name", sort: "asc" }] } }}
                        apiRef={apiRef}
                        getRowId={(row) => row.cus_id}
                        rows={rows}
                        columns={columns}
                        getRowClassName={(params) =>
                            params.indexRelativeToCurrentPage % 2 === 1 ? 'report-row-alternate' : ''
                        }
                        sx={{
                            '& .report-row-alternate .MuiDataGrid-cell, & .report-row-alternate .MuiDataGrid-filler--pinnedLeft': {
                                backgroundColor: '#EAF4FC',
                            },
                            '& .report-row-alternate:hover .MuiDataGrid-cell, & .report-row-alternate:hover .MuiDataGrid-filler--pinnedLeft': {
                                backgroundColor: '#DCEFFC',
                            },
                        }}
                        checkboxSelection={false}
                        disableRowSelectionOnClick
                        loading={isLoading}
                    />
                </div>
            </div>
            <Snackbar
                open={successText !== ""}
                autoHideDuration={30000}
                onClose={() => setSuccessText("")}
            >
                <Alert
                    sx={{ width: "100%" }}
                    severity={"success"}
                    elevation={6}
                    variant="filled"
                >
                    {successText}
                    <IconButton
                        aria-label="close"
                        color="inherit"
                        sx={{ p: 0.25 }}
                        onClick={() => setSuccessText("")}
                    >
                        <CloseIcon />
                    </IconButton>
                </Alert>
            </Snackbar>
            <Snackbar
                open={errorText !== ""}
                autoHideDuration={30000}
                onClose={() => setErrorText("")}
            >
                <Alert
                    sx={{ width: "100%" }}
                    severity={"error"}
                    elevation={6}
                    variant="filled"
                >
                    {errorText}
                    <IconButton
                        aria-label="close"
                        color="inherit"
                        sx={{ p: 0.25 }}
                        onClick={() => setErrorText("")}
                    >
                        <CloseIcon />
                    </IconButton>
                </Alert>
            </Snackbar>
        </div>
    );
}
